import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import type { Experience } from '../../types/api';

export const AdminExperiencesPage: React.FC = () => {
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();
    const [experiences, setExperiences] = useState<Experience[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editingExp, setEditingExp] = useState<Partial<Experience> | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    const loadExperiences = async () => {
        setLoading(true);
        try {
            const res = await api.adminGetExperiences();
            setExperiences(res.data);
        } catch {
            // error handled
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadExperiences();
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingExp) return;
        setSaving(true);
        try {
            await api.adminSaveExperience(editingExp.id || null, editingExp);
            setFlashMessage({
                type: 'success',
                text: `Leadership role successfully ${editingExp.id ? 'updated' : 'added'}.`
            });
            setEditingExp(null);
            loadExperiences();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to save experience.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete leadership record permanently?')) return;
        try {
            await api.adminDeleteExperience(id);
            setFlashMessage({ type: 'success', text: 'Leadership record deleted.' });
            loadExperiences();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to delete record.' });
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Leadership & Experiences</h1>
                    <p className="text-sm text-slate-400 mt-1">Verified records of executive student leadership, community roles, and milestones.</p>
                </div>

                <button 
                    type="button" 
                    onClick={() => setEditingExp({ role_title: '', organization_name: '', period: '', description: '', is_featured: false, is_published: true, sort_order: 0 })}
                    className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg transition cursor-pointer"
                >
                    + Add Leadership Role
                </button>
            </div>

            {/* Modal */}
            {editingExp && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-2xl overflow-y-auto max-h-[90vh]">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-white">
                                {editingExp.id ? 'Edit Leadership Record' : 'Add Leadership Record'}
                            </h2>
                            <button 
                                type="button" 
                                onClick={() => setEditingExp(null)}
                                className="text-slate-400 hover:text-white text-xl"
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Role Title</label>
                                <input 
                                    type="text" 
                                    required
                                    value={editingExp.role_title || ''}
                                    onChange={e => setEditingExp({ ...editingExp, role_title: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                    placeholder="e.g. President"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Organization Name</label>
                                <input 
                                    type="text" 
                                    required
                                    value={editingExp.organization_name || ''}
                                    onChange={e => setEditingExp({ ...editingExp, organization_name: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                    placeholder="e.g. English Knowledge Holders"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Period (Years / Duration)</label>
                                <input 
                                    type="text" 
                                    value={editingExp.period || ''}
                                    onChange={e => setEditingExp({ ...editingExp, period: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                    placeholder="e.g. 2024–2025"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Description</label>
                                <textarea 
                                    rows={4}
                                    value={editingExp.description || ''}
                                    onChange={e => setEditingExp({ ...editingExp, description: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                    placeholder="Describe key responsibilities and impact..."
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Organization URL</label>
                                <input 
                                    type="url" 
                                    value={editingExp.organization_url || ''}
                                    onChange={e => setEditingExp({ ...editingExp, organization_url: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                    placeholder="https://..."
                                />
                            </div>

                            <div className="flex flex-col gap-2 pt-2">
                                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={editingExp.is_featured ?? false}
                                        onChange={e => setEditingExp({ ...editingExp, is_featured: e.target.checked })}
                                        className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500" 
                                    />
                                    <span>Featured role (highlight with star on timeline)</span>
                                </label>
                                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={editingExp.is_published ?? true}
                                        onChange={e => setEditingExp({ ...editingExp, is_published: e.target.checked })}
                                        className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500" 
                                    />
                                    <span>Record is active and published</span>
                                </label>
                            </div>

                            <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
                                <button 
                                    type="button" 
                                    onClick={() => setEditingExp(null)}
                                    className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={saving}
                                    className="px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'Save Record'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Experiences Table */}
            <div className="p-6 rounded-3xl glass-card overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-800 text-xs text-slate-400 uppercase tracking-wider">
                            <th className="py-4 font-semibold">Role</th>
                            <th className="py-4 font-semibold">Organization</th>
                            <th className="py-4 font-semibold">Period</th>
                            <th className="py-4 font-semibold">Featured</th>
                            <th className="py-4 font-semibold">Status</th>
                            <th className="py-4 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {experiences.length > 0 ? (
                            experiences.map(exp => (
                                <tr key={exp.id} className="hover:bg-slate-800/20">
                                    <td className="py-4 font-bold text-white">{exp.role_title}</td>
                                    <td className="py-4 text-emerald-400 text-xs">{exp.organization_name}</td>
                                    <td className="py-4 text-xs text-slate-400">{exp.period || '—'}</td>
                                    <td className="py-4 text-xs">
                                        {exp.is_featured ? <span className="text-amber-400 font-bold">★ Featured</span> : '—'}
                                    </td>
                                    <td className="py-4">
                                        {exp.is_published ? (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                Active
                                            </span>
                                        ) : (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-800 text-slate-400 border border-slate-700">
                                                Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-4 text-right space-x-2">
                                        <button 
                                            type="button" 
                                            onClick={() => setEditingExp(exp)}
                                            className="px-2.5 py-1 rounded text-xs bg-emerald-950 text-emerald-300 border border-emerald-800 hover:bg-emerald-900 transition cursor-pointer"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => handleDelete(exp.id)}
                                            className="px-2.5 py-1 rounded text-xs bg-rose-950/60 text-rose-400 border border-rose-800/40 hover:bg-rose-900 transition cursor-pointer"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="py-8 text-center text-slate-500">
                                    {loading ? 'Loading leadership records...' : 'No leadership records found.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
